import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Subject, takeUntil } from 'rxjs';

import { CheckboxComponent } from '../../../shared/checkbox/checkbox.component';
import { InputComponent } from '../../../shared/input/input.component';
import { ModalShellComponent } from '../../../shared/modal-shell/modal-shell.component';
import { noWhitespaceValidator } from '../../../shared/validators/no-whitespace.validator';
import { SearchableObjectMultiSelectComponent } from '../../../shared/searchable-object-multi-select/searchable-object-multi-select.component';
import { resolveErrorMessage } from '../../../shared/utils/api-error.util';

import { AppService } from '../../../services/application/app.service';
import { MessageModalService } from '../../../services/message-modal/message-modal.service';

import { Application, ApplicationData, AppVersion, CreateVersionPayload, Org } from '../../../interfaces/application';

import { APP_VERSION_CONTAINER_NAME_MAX_LENGTH, APP_VERSION_REGEX } from '../shared-form-elements';

interface VersionForm {
  baseApp: FormControl<ApplicationData | null>;
  version: FormControl<string | null>;
  containerName: FormControl<string | null>;
  inputType: FormControl<string | null>;
  dependsOnAppVersions: FormControl<AppVersion[] | null>;
  multiRun: FormControl<boolean | null>;
}

export type AddAppVersionModalProps = {
  title: string;
  allApplications: ApplicationData[];
  allAppVersions: AppVersion[];
  allOrgs: Org[];
};

@Component({
  selector: 'app-add-app-version-modal',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    MatFormFieldModule,
    CheckboxComponent,
    InputComponent,
    ModalShellComponent,
    SearchableObjectMultiSelectComponent,
  ],
  templateUrl: './add-app-version-modal.component.html',
  styleUrl: './add-app-version-modal.component.css',
})
export class AddAppVersionModalComponent implements OnInit, OnDestroy {
  versionForm!: FormGroup<VersionForm>;

  title!: AddAppVersionModalProps['title'];
  allApplications!: AddAppVersionModalProps['allApplications'];
  allAppVersions!: AddAppVersionModalProps['allAppVersions'];
  allOrgs!: AddAppVersionModalProps['allOrgs'];

  isErrorVisible: boolean = false;
  errorMessage: string = '';

  private destroy$ = new Subject<void>();

  // base Application
  selectBaseApplicationLabel = $localize`Application`;
  selectBaseApplicationPlaceholder = $localize`Select one application`;
  appSelectOptions: ApplicationData[] = [];
  appSelectDisplayFn = (app: ApplicationData): string => app.name;
  appSelectCompareFn = (a: ApplicationData, b: ApplicationData): boolean => a.appId === b.appId;

  // version to be created
  appVersionLabel = $localize`Application Version`;
  appVersionPlaceholder = $localize`X.Y.Z or W.X.Y.Z`;

  // container for new version
  containerNameLabel = $localize`Container Name`;
  containerNamePlaceholder = $localize`Enter Container Name`;

  // input type
  inputTypeLabel = $localize`Input Type`;
  inputTypeSubLabel = $localize`(e.g., config, SV_panel, RepExp_panel)`;

  // versioned Applications that this version depends on
  dependsOnAppsLabel = $localize`Depends On Prerequisite Applications`;
  dependsOnAppsPlaceholder = $localize`None`;
  dependsOnAppsSelectOptions: AppVersion[] = [];
  dependsOnAppsSelectDisplayFn = (app: AppVersion): string => `${app.name} ${app.version}`;
  dependsOnAppsSelectCompareFn = (a: AppVersion, b: AppVersion): boolean => a.appVersionId === b.appVersionId;

  // whether this version can run against the combined output of multiple instrument runs
  multiRunLabel = $localize`Multi-Run Support`;
  multiRunSubLabel = $localize`This application supports multiple instrument runs in its input sample (checked) or only a single instrument run (unchecked).`;

  constructor(
    public dialogRef: MatDialogRef<AddAppVersionModalComponent>,
    @Inject(MAT_DIALOG_DATA) data: AddAppVersionModalProps,
    private fb: FormBuilder,
    private appService: AppService,
    private messageModal: MessageModalService
  ) {
    this.title = data.title;
    this.allApplications = data.allApplications;
    this.appSelectOptions = data.allApplications;
    this.allAppVersions = data.allAppVersions;
    this.dependsOnAppsSelectOptions = data.allAppVersions;
    this.allOrgs = data.allOrgs;
  }

  ngOnInit(): void {
    this.buildForm();
  }

  private buildForm(): void {
    this.versionForm = this.fb.group({
      baseApp: [null as ApplicationData | null, Validators.required],
      version: ['', [Validators.required, Validators.pattern(APP_VERSION_REGEX), noWhitespaceValidator()]],
      containerName: [
        '',
        [Validators.required, Validators.maxLength(APP_VERSION_CONTAINER_NAME_MAX_LENGTH), noWhitespaceValidator()],
      ],
      inputType: ['', noWhitespaceValidator()],
      dependsOnAppVersions: [[] as AppVersion[]],
      multiRun: [true],
    });
  }

  closePopup(): void {
    this.dialogRef.close();
  }

  handleSubmit(): void {
    this.isErrorVisible = false;

    if (this.versionForm.invalid) {
      this.versionForm.markAllAsTouched();
      return;
    }

    const { baseApp, version, containerName, inputType, dependsOnAppVersions, multiRun } =
      this.versionForm.getRawValue();

    if (!baseApp || !version || !containerName) {
      this.handleApiError($localize`Selected application could not be found. Please try again.`, '');
      return;
    }

    // The full application object is the control value, so read the ID directly.
    const applicationId = baseApp.appId;

    // version and containerName are required, so they are non-null once the form is valid.
    const payload: CreateVersionPayload = {
      version: version,
      containerName: containerName,
      multiRun: multiRun ?? false,
    };

    if (inputType) {
      payload.inputs = inputType;
    }

    if (dependsOnAppVersions?.length) {
      payload.prerequisiteApps = dependsOnAppVersions.map((appVersion) => Number(appVersion.appVersionId));
    }

    this.appService
      .createAppVersion(Number(applicationId), payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          if (response.success) {
            this.dialogRef.close({ success: true, action: 'version' });
          }
        },
        error: (err: any) => {
          console.error('A server error occurred while creating the new version.', err);
          this.handleApiError(err, $localize`An error occurred while creating the new version.`);
        },
      });
  }

  // Helper function for error handling
  private handleApiError(error: any, fallbackMessage: string): void {
    this.errorMessage = resolveErrorMessage(error, fallbackMessage);
    this.isErrorVisible = true;
  }

  get baseApp() {
    return this.versionForm.controls.baseApp;
  }
  get version() {
    return this.versionForm.controls.version;
  }
  get containerName() {
    return this.versionForm.controls.containerName;
  }
  get inputType() {
    return this.versionForm.controls.inputType;
  }
  get multiRun() {
    return this.versionForm.controls.multiRun;
  }

  // CheckboxComponent is not a ControlValueAccessor, so bind it to the reactive control manually.
  onMultiRunChange(checked: boolean): void {
    this.multiRun.setValue(checked);
    this.multiRun.markAsDirty();
  }

  isFormChanged(): boolean {
    return this.versionForm.dirty;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
